import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { Button } from "../components/Button";
import { Dialog, DialogClose, DialogContent } from "../components/Dialog";
import { ImageWrapper } from "../components/ImageWrapper";
import { Spinner } from "../components/Spinner";
import { ChevronLeft, ChevronRight, Download01, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import React, { useCallback, useState } from "react";
function downloadFile(url, filename) {
  if (!url.startsWith("https://") && !url.startsWith("http://") && !url.startsWith("blob:") && !url.startsWith("data:")) {
    return;
  }
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
/**
 * A full-screen overlay for viewing an image at a larger size, controlled via
 * `open` / `onOpenChange`, with an optional download affordance and optional
 * previous/next gallery navigation. Use it to let users inspect an image at
 * full resolution; for non-image modal content, use Dialog or Sheet instead.
 *
 * @summary Full-screen image zoom dialog.
 */
function ImageZoomDialog({
  open,
  onOpenChange,
  image,
  navigation
}) {
  var _image$alt;
  const {
    _: _t
  } = _useLingui();
  const [imageLoaded, setImageLoaded] = useState(false);
  const handleDownload = useCallback(e => {
    e.stopPropagation();
    if (image.downloadUrl && image.title) {
      downloadFile(image.downloadUrl, image.title);
    }
  }, [image.downloadUrl, image.title]);
  React.useEffect(() => {
    setImageLoaded(false);
  }, [open, image.src]);
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onOpenChange: onOpenChange
  }, /*#__PURE__*/React.createElement(DialogContent, {
    size: "fit",
    className: "overflow-hidden p-3 w-fit"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative flex items-center justify-center gap-2"
  }, (navigation === null || navigation === void 0 ? void 0 : navigation.hasPrevious) && /*#__PURE__*/React.createElement(Button, {
    variant: "ghost",
    size: "sm",
    icon: ChevronLeft,
    onClick: e => {
      e.stopPropagation();
      navigation.onPrevious();
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "relative rounded overflow-hidden"
  }, image.isLoading ? /*#__PURE__*/React.createElement("div", {
    className: cn("mx-auto flex aspect-square w-full min-w-[50vh]", "max-w-[80vh] items-center justify-center", "bg-muted-background")
  }, /*#__PURE__*/React.createElement(Spinner, {
    variant: "dark",
    size: "lg"
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ImageWrapper, {
    src: image.src,
    alt: (_image$alt = image.alt) !== null && _image$alt !== void 0 ? _image$alt : "",
    className: "max-h-[calc(90vh-1.5rem)] max-w-[calc(90vw-1.5rem)]",
    onLoad: () => setImageLoaded(true)
  }), /*#__PURE__*/React.createElement(DialogClose, {
    asChild: true
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outline",
    size: "xs",
    icon: XClose,
    className: "absolute right-2 top-2"
  })), imageLoaded && image.downloadUrl && /*#__PURE__*/React.createElement(Button, {
    variant: "outline",
    size: "xs",
    icon: Download01,
    tooltip: _t(
    /** i18n */
    {
      id: "mzI_c-",
      message: "Download"
    }),
    className: "absolute bottom-2 right-2",
    onClick: handleDownload
  }))), (navigation === null || navigation === void 0 ? void 0 : navigation.hasNext) && /*#__PURE__*/React.createElement(Button, {
    variant: "ghost",
    size: "sm",
    icon: ChevronRight,
    onClick: e => {
      e.stopPropagation();
      navigation.onNext();
    }
  }))));
}
ImageZoomDialog.displayName = "ImageZoomDialog";
export { downloadFile, ImageZoomDialog };
//# sourceMappingURL=ImageZoomDialog.js.map